import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash, MagnifyingGlass, ArrowSquareOut, Info, Copy, Check } from "@phosphor-icons/react";

/**
 * Verzeichnis kuratierter Abfallwirtschaftsbetriebe (Fallback wenn eigene API nicht funktioniert).
 * Nach Auswahl öffnet sich die Original-Seite in neuem Tab, Mandant kann die ICS-URL kopieren
 * und beim Kunden im Feld `abfall_calendar_url` hinterlegen.
 */
export default function WasteProvidersPanel() {
  const [providers, setProviders] = useState([]);
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/waste-providers").then((r) => setProviders(r.data.providers || []));
  }, []);

  const filtered = useMemo(() => {
    if (!q) return providers;
    const qn = q.toLowerCase();
    return providers.filter((p) => p.name.toLowerCase().includes(qn) || p.region.toLowerCase().includes(qn));
  }, [providers, q]);

  const copyUrl = async (url, key) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) { console.warn("[WasteProvidersPanel] load failed", e); }
  };

  return (
    <section className="bg-white border border-black/10 p-5 rounded-lg" data-testid="waste-providers-panel">
      <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
        <Trash size={18} weight="bold" /> Abfallwirtschaften — Kalender-Direktzugriff
      </h3>
      <p className="text-xs text-zinc-500 mb-4">
        Öffnen Sie den offiziellen Abfallkalender der zuständigen Kommune, laden Sie die ICS-Datei herunter und hinterlegen Sie den ICS-Link direkt beim Kunden (Kundenprofil → „Abfallkalender-URL"). So sind Rest-, Bio- und Papiermüll-Termine immer korrekt — auch wenn eine Müllabfuhr-API mal nicht zuverlässig läuft.
      </p>

      <div className="relative mb-4">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input className="h-10 rounded-none pl-9" placeholder="Stadt oder Betrieb suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="waste-search-input" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="waste-providers-list">
        {filtered.map((p) => (
          <article key={p.key} className="p-4 border border-black/10 rounded hover:shadow-md transition-shadow bg-white" data-testid={`waste-provider-${p.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{p.region}</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-zinc-600 leading-snug italic">{p.ics_hint}</div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded bg-zinc-900 text-white hover:bg-black"
                data-testid={`waste-open-${p.key}`}
              >
                <ArrowSquareOut size={12} weight="bold" /> Öffnen
              </a>
              <button
                type="button"
                onClick={() => copyUrl(p.url, p.key)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-black/10 hover:bg-zinc-50"
                data-testid={`waste-copy-${p.key}`}
              >
                {copied === p.key ? <><Check size={12} weight="bold" className="text-emerald-600" /> Kopiert</> : <><Copy size={12} weight="bold" /> Link kopieren</>}
              </button>
            </div>
          </article>
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="text-center text-sm text-zinc-500 py-6 italic">Kein Treffer. Falls Ihre Stadt fehlt, senden Sie uns einen Hinweis — wir nehmen sie auf.</div>
      )}

      <div className="mt-6 flex items-start gap-2 p-3 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900">
        <Info size={14} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <strong>So funktioniert's:</strong> Öffnen Sie die Seite, geben Sie die Adresse des Kunden ein, laden Sie die ICS-Datei herunter (oder kopieren Sie den Abonnement-Link).
          Öffnen Sie dann den Kunden im Tab „Kunden" → Feld „Abfallkalender-URL" → fügen Sie die ICS-URL ein.
        </div>
      </div>
    </section>
  );
}
